import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { capitalize, debounce } from 'lodash';
import PropTypes from 'prop-types';

import { Box, LinkButton, Flex, Link, Text } from 'core/components';
import $app from 'app/stores/$app';
import DataViewWrapper from 'app/components/dataviews/DataViewWrapper';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import {
  applyFilterGroupsToFbdQuery,
  applyFiltersToFbdQuery,
  getInternalFilters
} from 'app/components/filters/simple/simpleFilterUtils';

import { addFilters } from 'app/stores/query/FilterUtils';
import $rbac from 'app/stores/rbac/$rbac';
import { greekIt } from 'app/util/utils';
import { AGGREGATE_LABELS } from 'app/constants/aggregateLabels';
import { formatLookbackTime } from 'core/util/dateUtils';
import Tabs from './Tabs';

const CONTEXT_TYPE_TABBED_CHART = 'tabbed_chart';

function applyTotalFilters(query, filters, filterGroups) {
  if (!query.filters) {
    query.filters = {
      connector: 'All',
      filterGroups: [
        ...filterGroups,
        {
          connector: 'All',
          not: false,
          named: false,
          name: '',
          filterGroups: [],
          autoAdded: true,
          filters: getInternalFilters(filters)
        }
      ]
    };
  } else {
    query.filters.filterGroups.push({
      connector: 'All',
      filters: getInternalFilters(filters),
      not: false,
      named: false,
      name: '',
      filterGroups,
      autoAdded: true
    });
  }
}

function getTabQuery({
  $query,
  queryName,
  overrides,
  filters,
  simpleFilters = [],
  simpleFilterGroups = [],
  immutableSimpleFilters = [],
  metrics,
  tabName,
  topxMetrics,
  topxMetricField
}) {
  const tabQuery = $query.get(queryName, overrides);
  if (filters && filters.length > 0) {
    addFilters(tabQuery, filters);
  }

  if (simpleFilters) {
    applyFiltersToFbdQuery(tabQuery, simpleFilters);
  }
  if (immutableSimpleFilters) {
    applyFiltersToFbdQuery(tabQuery, immutableSimpleFilters);
  }
  if (simpleFilterGroups) {
    applyFilterGroupsToFbdQuery(tabQuery, simpleFilterGroups);
  }

  tabQuery.metric = metrics[tabName] || 'Traffic';
  if (tabName === 'Total') {
    if (!$app.isSubtenant && queryName !== 'interfaceIngressEgressQuery') {
      tabQuery.filterDimensionsEnabled = false;
      tabQuery.filterDimensionSort = false;
      tabQuery.metric = ['simple_trf_prof'];
    }
  } else {
    tabQuery.filterDimensionsEnabled = false;
    tabQuery.filterDimensionSort = false;
  }

  if (tabName === 'Ingress' || tabName === 'Egress') {
    tabQuery.filterDimensionsEnabled = true;
    tabQuery.filterDimensionSort = true;
    tabQuery.show_total_overlay = false;
  }

  if (tabName === 'Inbound' || tabName === 'Ingress') {
    $query.promoteFbdFilterGroup(tabQuery, 'Inbound', 'Ingress');
  } else if (tabName === 'Outbound' || tabName === 'Egress') {
    $query.promoteFbdFilterGroup(tabQuery, 'Outbound', 'Egress');
  } else if (tabName === 'Internal') {
    if (topxMetricField) {
      // If there's a topxMetricField but no topxMetrics (yet), we don't query (yet)
      if (!topxMetrics) {
        return null;
      }

      tabQuery.aggregateFiltersEnabled = true;
      tabQuery.aggregateFilters = [
        {
          name: 'Internal',
          named: true,
          connector: 'All',
          not: false,
          autoAdded: '',
          metric: metrics[tabName],
          filters: $query.getFilterDimensionGroupFilters(tabQuery, 'Internal')
        }
      ];

      addFilters(
        tabQuery,
        topxMetrics.Internal.map((filterValue) => ({
          filterField: topxMetricField,
          operator: '=',
          filterValue
        })),
        'Any'
      );
    } else {
      $query.promoteFbdFilterGroup(tabQuery, 'Internal');
    }
  } else if (tabName === 'Through') {
    $query.promoteFbdFilterGroup(tabQuery, 'Through');
  } else if (tabName === 'Other') {
    $query.promoteFbdFilterGroup(tabQuery, 'Other');
  } else if (simpleFilters.length > 0 || immutableSimpleFilters.length > 0 || simpleFilterGroups.length > 0) {
    applyTotalFilters(tabQuery, [...simpleFilters, ...immutableSimpleFilters], simpleFilterGroups);
  }

  return tabQuery;
}

@inject('$colors', '$explorer', '$query', '$journeys')
@observer
class TabbedChart extends Component {
  static propTypes = {
    contextName: PropTypes.string,
    contextType: PropTypes.string,
    contextEnabled: PropTypes.bool,
    onDataUpdate: PropTypes.func,
    childrenLocation: PropTypes.string,
    metrics: PropTypes.object,
    queryName: PropTypes.string,
    height: PropTypes.number,
    viewInExplorer: PropTypes.bool,
    simpleFilters: PropTypes.array,
    simpleFilterGroups: PropTypes.array,
    immutableSimpleFilters: PropTypes.array,
    zoomEnabled: PropTypes.bool,
    isWidget: PropTypes.bool
  };

  static defaultProps = {
    contextName: 'Traffic Chart',
    contextType: CONTEXT_TYPE_TABBED_CHART,
    contextEnabled: true,
    onDataUpdate: undefined,
    childrenLocation: 'bottom',
    metrics: {},
    queryName: 'inboundOutboundInternalQuery',
    height: 250,
    viewInExplorer: true,
    simpleFilters: [],
    simpleFilterGroups: [],
    immutableSimpleFilters: [],
    zoomEnabled: false,
    isWidget: false
  };

  static getDerivedStateFromProps(props, state) {
    const {
      $query,
      queryName,
      overrides,
      filters,
      simpleFilters,
      simpleFilterGroups,
      immutableSimpleFilters,
      metrics,
      topxMetrics,
      topxMetricField
    } = props;
    let { tabName } = state;

    if (
      !tabName ||
      queryName !== state.queryName ||
      overrides !== state.overrides ||
      filters !== state.filters ||
      topxMetrics !== state.topxMetrics ||
      immutableSimpleFilters !== state.immutableSimpleFilters
    ) {
      const query = $query.get(queryName, overrides);
      if (filters && filters.length > 0) {
        addFilters(query, filters);
      }
      if (simpleFilters && simpleFilters.length > 0) {
        applyFiltersToFbdQuery(query, simpleFilters);
      }
      if (immutableSimpleFilters && immutableSimpleFilters.length > 0) {
        applyFiltersToFbdQuery(query, immutableSimpleFilters);
      }
      if (simpleFilterGroups && simpleFilterGroups.length > 0) {
        applyFilterGroupsToFbdQuery(query, simpleFilterGroups);
      }
      if (simpleFilters.length > 0 || immutableSimpleFilters.length > 0 || simpleFilterGroups.length > 0) {
        applyTotalFilters(query, [...simpleFilters, ...immutableSimpleFilters], simpleFilterGroups);
      }

      tabName = tabName || 'Total';

      const tabQuery = getTabQuery({
        $query,
        queryName,
        overrides,
        filters,
        simpleFilters,
        simpleFilterGroups,
        immutableSimpleFilters,
        metrics,
        tabName,
        topxMetrics,
        topxMetricField
      });

      return {
        tabName,
        tabQuery,
        query,
        queryName,
        overrides,
        filters,
        topxMetrics,
        immutableSimpleFilters,
        dataview: null
      };
    }

    return null;
  }

  state = {
    chartSummary: null // Stores traffic data for AI context
  };

  contextUpdateCount = 0; // Track update frequency to prevent loops (instance variable, not state)

  contextUpdateTimer = null; // Timer for resetting counter

  componentDidMount() {
    this.updateChartContext();
  }

  componentWillUnmount() {
    this.removeChartContext();
    // Clean up timer
    if (this.contextUpdateTimer) {
      clearTimeout(this.contextUpdateTimer);
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    const { height, availableWidth, overrides, simpleFilters } = this.props;
    const { chartSummary } = this.state;

    if (prevProps.height !== height || prevProps.availableWidth !== availableWidth) {
      this.reflow();
    }

    const { tabQuery, dataLink } = this.state;
    if (tabQuery && tabQuery !== prevState.tabQuery) {
      const newDataLink = await this.getDataLink();
      if (newDataLink !== dataLink) {
        // eslint-disable-next-line
        this.setState({ dataLink: newDataLink });
      }
    }

    // Update context when data or settings change
    // Only check changes that should trigger context updates (not internal state like dataLink)
    const chartDataChanged = chartSummary !== prevState.chartSummary;
    const overridesChanged = overrides !== prevProps.overrides;
    const filtersChanged = simpleFilters !== prevProps.simpleFilters;

    if (chartDataChanged || overridesChanged || filtersChanged) {
      this.updateChartContext();
    }
  }

  /**
   * Handles chart data updates from Tabs component
   */
  handleChartDataUpdate = (chartData) => {
    const { onDataUpdate } = this.props;

    if (chartData) {
      this.setState({ chartSummary: chartData });
    }

    // Also notify parent if they provided a callback (backwards compatibility)
    if (onDataUpdate) {
      onDataUpdate(chartData);
    }
  };

  /**
   * Updates AI context with current chart state
   */
  updateChartContext = () => {
    const { $journeys, contextName, contextType, contextEnabled, overrides } = this.props;
    const { chartSummary } = this.state;

    // Prevent infinite loops - if we're updating too frequently, something is wrong
    if (this.contextUpdateCount > 10) {
      console.error('🚨 [AI Context] Too many updates, possible infinite loop detected. Stopping updates.');
      return;
    }

    this.contextUpdateCount += 1;
    // Reset counter after a delay
    if (this.contextUpdateTimer) {
      clearTimeout(this.contextUpdateTimer);
    }
    this.contextUpdateTimer = setTimeout(() => {
      this.contextUpdateCount = 0;
    }, 1000);

    if (!contextEnabled || !$journeys) {
      return;
    }

    const contextParts = [];
    contextParts.push(`Context: ${contextName}`);

    // Add time range if available
    if (overrides?.lookback_seconds) {
      const timeDisplay = formatLookbackTime(overrides.lookback_seconds);
      contextParts.push(`Time Range: ${timeDisplay}`);
    }

    // Add aggregation type if available
    if (overrides?.aggregateTypes && overrides.aggregateTypes.length > 0) {
      const aggregateType = overrides.aggregateTypes[0];
      const aggregateDisplay = AGGREGATE_LABELS[aggregateType] || aggregateType;
      contextParts.push(`Aggregation: ${aggregateDisplay}`);
    }

    // Add current traffic values
    if (chartSummary && Object.keys(chartSummary).length > 0) {
      contextParts.push('\nCurrent Traffic Values:');

      // Format each traffic category
      Object.entries(chartSummary).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
          const formattedValue = this.formatTrafficValue(value, overrides?.aggregateTypes?.[0]);
          contextParts.push(`  ${formattedKey}: ${formattedValue}`);
        }
      });
    } else {
      contextParts.push('\nTraffic Values: Loading...');
    }

    const contextText = contextParts.join('\n');

    $journeys.updateChatContext({
      type: contextType,
      name: contextName,
      context: contextText,
      placeholder: 'Ask about traffic patterns',
      hidden: false
    });
  };

  /**
   * Formats traffic values for display
   */
  formatTrafficValue = (value, aggregateType) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    let result;

    // If no aggregate type specified, default to bits per second (common case for traffic)
    if (!aggregateType) {
      result = greekIt(value, { suffix: 'bps' });
    } else {
      // Handle different aggregate types
      switch (aggregateType) {
        case 'bits_per_second':
        case 'p95th_bits_per_sec':
        case 'avg_bits_per_sec':
        case 'max_bits_per_sec':
        case 'sum_bits_per_sec':
          result = greekIt(value, { suffix: 'bps' });
          break;
        case 'packets_per_second':
        case 'sum_packets':
          result = greekIt(value, { suffix: 'pps' });
          break;
        case 'bytes':
        case 'sum_bytes':
          result = greekIt(value, { suffix: 'B' });
          break;
        case 'unique_src_ip':
        case 'unique_dst_ip':
          result = greekIt(value, { suffix: 'IPs' });
          break;
        default:
          result = greekIt(value, { suffix: '' });
      }
    }

    // greekIt returns an object with display and unitsLabel
    // Concatenate them for a complete string
    return result.display + (result.unitsLabel || '');
  };

  /**
   * Removes AI context when component unmounts
   */
  removeChartContext = () => {
    const { $journeys, contextType, contextEnabled } = this.props;

    if (!contextEnabled || !$journeys) {
      return;
    }

    const oldContext = Array.from($journeys.activeChatContext).find((ctx) => ctx.type === contextType);
    if (oldContext) {
      $journeys.removeChatContext(oldContext);
    }
  };

  async getDataLink() {
    const { $explorer } = this.props;
    const { tabQuery } = this.state;
    return $explorer.getQueryPath(tabQuery);
  }

  reflow = debounce(() => {
    const { dataview } = this.state;

    if (dataview) {
      dataview.reflow();
    }
  }, 250);

  handleTabChange = (tabName) => {
    const {
      $query,
      queryName,
      overrides,
      filters,
      metrics,
      topxMetrics,
      topxMetricField,
      simpleFilters,
      simpleFilterGroups,
      immutableSimpleFilters
    } = this.props;

    const tabQuery = getTabQuery({
      $query,
      queryName,
      overrides,
      filters,
      metrics,
      tabName,
      topxMetrics,
      topxMetricField,
      simpleFilters,
      simpleFilterGroups,
      immutableSimpleFilters
    });

    this.setState({ tabName, tabQuery });
  };

  handleDataViewCreate = (dataview) => {
    this.setState({ dataview });
  };

  render() {
    const {
      $colors,
      children,
      childrenLocation,
      height,
      metrics,
      queryName,
      showNativeLegend,
      viewInExplorer,
      viewProps /* , noTrend */,
      zoomEnabled,
      isWidget
    } = this.props;
    const { query, tabName, tabQuery, dataLink } = this.state;

    const tabNames = query.filterDimensions.filterGroups.map((group) => group.name);

    if (!$app.isSubtenant && queryName !== 'interfaceIngressEgressQuery') {
      // force top level query for tab values (query) to not use FBD ('total' tabQuery already taken care of)
      query.filterDimensionsEnabled = false;
      query.filterDimensionSort = false;
      query.metric = ['simple_trf_prof'];
    }

    return (
      <Box>
        <Flex borderBottom="tabs" mx={isWidget ? '-1px' : '0px'} bg="theme.colors.appBackground" mb="-1px">
          <LightweightDataViewWrapper query={query}>
            {({ results, queryModel }) => (
              <Tabs
                data={results.getRawData(true)}
                aggregatePrefix={results.prefix}
                queryModel={queryModel}
                queryResults={results}
                viewProps={{ ...viewProps }}
                onTabChange={this.handleTabChange}
                onDataUpdate={this.handleChartDataUpdate}
                noTrend //= {noTrend} override prop and just turn off for now
              />
            )}
          </LightweightDataViewWrapper>
          {childrenLocation === 'top' && children}
        </Flex>
        <Box
          border={isWidget ? 'none' : 'tabs'}
          pt={isWidget ? '2px' : 'none'}
          borderTop="none"
          borderRadius="0 0 4px 4px"
        >
          <Box position="relative" width="100%" p={2} pb={0} minHeight={height}>
            {tabQuery && (
              <DataViewWrapper
                key={`${tabName}-${showNativeLegend}`} // Rerenders legend upon tab change
                query={tabQuery}
                onDataViewCreate={this.handleDataViewCreate}
                viewProps={{
                  height,
                  ...viewProps,
                  colors: $colors.getAllTabbedChartColors(tabName, tabNames, !!metrics[tabName]),
                  getColorIndex: $colors.getTabColorIndex,
                  showNativeLegend: showNativeLegend && tabName !== 'Total',
                  showAxisLabels: false,
                  chartConfig: {
                    chart: zoomEnabled ? {} : { zoomType: '' },
                    tooltip: {
                      seriesNameFormatter: (value) => capitalize(value)
                    }
                  }
                }}
                allowCache
              />
            )}
          </Box>
          <Flex alignItems="center" justifyContent="flex-end" p={1} pt={0}>
            {tabName === 'Other' && (
              <Text flex={1} muted small>
                Other designates traffic that is neither outbound, inbound, through or internal. This is usually a
                result of a low{' '}
                <Link
                  to={
                    $rbac.hasUnrestrictedPermissions(['device::read'])
                      ? '/v4/settings/interface-classification'
                      : '/v4/settings/interfaces'
                  }
                >
                  interface classification
                </Link>{' '}
                rate.
              </Text>
            )}
            {viewInExplorer && !$app.isExport && !$app.isSubtenant && (
              <LinkButton icon="series-search" text="View in Data Explorer" to={dataLink} minimal small />
            )}
            {childrenLocation === 'bottom' && children}
          </Flex>
        </Box>
      </Box>
    );
  }
}

export default TabbedChart;
